import * as Sentry from '@sentry/react';
import { HttpStatusCode, type AxiosError } from 'axios';
import env from '../utils/env';
import { httpHeaders } from '../utils/consts/httpHeaders';

const errorBlockList = [
  "Cannot read properties of null (reading 'value')", // Chrome+React error described in src/utils/preventTranslationCrash.ts
  "null is not an object (evaluating 'document.getElementById('name').value')", // same as above
  'growsumo is not defined', // some sort of GTM tracker
  "Can't find variable: growsumo", // some sort of GTM tracker
  'this.clear is not a function', // GTM stuff
  'Failed to fetch (ad.doubleclick.net)', // GTM stuff
];

const initializeSentry = () => {
  Sentry.init({
    dsn: 'https://3ad88f33d9394f2fbf6906d45faff8ff@o36975.ingest.sentry.io/5860925',
    environment: env.NODE_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.thirdPartyErrorFilterIntegration({
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
        filterKeys: [env.SENTRY_APPLICATION_TAG],
      }),
    ],
    tracesSampleRate: 0.1,
    ignoreErrors: errorBlockList,
    denyUrls: [
      /chrome:\/\//,
      /moz-extension/,
      /chrome-extension/,
      /safari-extension/,
      /safari-web-extension/,
      /webkit-masked-url/, // safari
      /gtag\/js/, // google analytics
    ],
  });
};

const logError = (error: Error, extra?: Record<string, unknown>) =>
  Sentry.captureException(error, {
    extra,
  });

const logApiError = (error: AxiosError) => {
  if (!error.config) {
    Sentry.captureException(new Error('API Error w/o config', { cause: error }));

    return;
  }

  if (
    error.status &&
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- these are numbers, mate, cmon
    (error.status < HttpStatusCode.BadRequest || error.status >= HttpStatusCode.InternalServerError)
  ) {
    Sentry.captureException(
      new Error(`API Exception ${error.config.method} ${error.config.url}: ${error.status}`, { cause: error }),
      {
        extra: {
          traceId: error.config.headers[httpHeaders.TRACE_ID],
          response: error.response?.data ? JSON.stringify(error.response.data) : null,
        },
      },
    );
  }
};

const logMessage = (message: string, extra: Record<string, unknown>) => Sentry.captureMessage(message, { extra });

export { initializeSentry, logError, logApiError, logMessage };
